import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { okOrShowError, showError } from 'components/notify';
import { usePortal } from '../portal/portal';
import { groupBy, pick, sortBy } from 'remeda';
import { compactObj, groupMapObj, keyBy, tree } from 'shared/collection';
import { notNull, parseOrDefault, zx } from 'shared/type';
import { z } from 'zod';
import { DateTime } from 'luxon';
import { useQuerySb, useQuerySbRead, useQuerySbSync, UseQuerySbSyncProps } from 'utils/db/useQuerySb';
import { O } from 'ts-toolbelt';
import { ProfileUI } from 'components/portal/profileTypes';
import { useStickyOrder } from 'components/hooks/useStickyOrder';
import { sb } from 'utils/db';
import { CommentReaction } from './CommentReaction';
import { CommentPost, CommentPut, CommentRow, zCommentRow, zCommentStatus } from './commentTypes';
import { Notify } from 'api/notify';
import { zQuestionQuery } from 'components/question/questionComponentTypes';
import { useQuestionUIItems } from 'components/question/useQuestions';
import { useLocalStorage } from '@mantine/hooks';
const fetchComments = async ({
  question_id
}: CommentQuestion) => {
  let q = sb.from('comment').select(`*`).match({
    question_id
  });
  const {
    data,
    error
  } = await q.order('created_at', {
    ascending: true
  });
  if (error) throw Error(error.message);
  return data.map(c => zCommentRow.parse(c));
};
export type CommentFetchedEx = Awaited<ReturnType<typeof fetchComments>>[number];
export type UseComments = ReturnType<typeof useComments>;
export type CommentItem<T = {}> = CommentFetchedEx & T & {
  children: CommentItem<T>[];
  depth: number;
  profile?: ProfileUI;
} & {
  reactionAgg: CommentReaction.ReactionAggCfg;
};

/** details from a question that comment functionality needs */
export type CommentQuestion = {
  question_id: string;
  created_by: string;
  title?: string;
};
export type UseCommentPost = O.Optional<CommentPost, 'question_id'> | CommentPut;
export const isCommentPut = (c: UseCommentPost): c is CommentPut => 'id' in c;
const commentsQueryProps = ({
  question_id,
  disabled,
  subquestion_id,
  created_by
}: {
  question_id?: string;
  disabled?: boolean;
  subquestion_id?: string | null;
  created_by?: string;
}) => ({
  table: 'comment',
  filter: {
    question_id,
    deleted: false
  },
  options: {
    enabled: question_id != null && !disabled
  },
  schema: zCommentRow,
  select: b => b.select(`*`).match({
    question_id
  }).order('created_at', {
    ascending: true
  }),
  // re-use the same query cache,
  clientFilter: (r: CommentRow) => (!created_by || r.created_by == created_by) && (subquestion_id === undefined || r.subquestion_id == subquestion_id)
}) as UseQuerySbSyncProps<typeof zCommentRow.shape>;
export const useCommentsQuery = t0 => {
  const $ = _c(4);
  const {
    question_id,
    subquestion_id,
    disabled
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== question_id || $[2] !== subquestion_id) {
    t1 = commentsQueryProps({
      question_id,
      disabled,
      subquestion_id
    });
    $[0] = disabled;
    $[1] = question_id;
    $[2] = subquestion_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useQuerySbRead(t1);
};
export const useComments = ({
  question_id,
  subquestion_id,
  created_by,
  disabled
}: {
  question_id?: string;
  subquestion_id?: string | null;
  created_by?: string;
  disabled?: boolean;
}) => {
  const {
    profile,
    profilesUI
  } = usePortal();
  const {
    data: rawComments,
    insert,
    update,
    isPending,
    isLoading
  } = useQuerySbSync(commentsQueryProps({
    question_id,
    disabled,
    subquestion_id,
    created_by
  }));
  const reactionAggState = useQuestionUIItems({
    schema: CommentReaction.zCfgAgg,
    mode: 'workspace'
  });
  const reactionAggByCommentId = useMemo(() => keyBy(reactionAggState ?? [], r => r.itemId), [reactionAggState]);
  const commentsVanillaOrder = useMemo(() => {
    if (!rawComments) return undefined;
    const comments = rawComments.map(cs => ({
      ...cs,
      profile: profilesUI?.[cs.created_by],
      reactionAgg: reactionAggByCommentId[cs.id]?.cfg
    }));
    const byParentId = groupBy(comments ?? [], c => c.parent_id ?? '_root');
    const filterDeleted = (c_0: CommentItem) => !c_0.deleted || c_0.children?.length;
    const addChildren = (comment: (typeof comments)[number], depth: number): CommentItem => ({
      ...comment,
      children: sortBy(byParentId[comment.id]?.map(c_1 => addChildren(c_1, depth + 1))?.filter(filterDeleted) ?? [], [c_2 => DateTime.fromISO(c_2.created_at).toMillis(), 'asc']),
      depth
    });
    return comments.filter(c_3 => c_3.parent_id == null).map(c_4 => addChildren(c_4, 0)).filter(filterDeleted);
  }, [rawComments, profilesUI]);
  const {
    data: comments_0
  } = useStickyOrder(commentsVanillaOrder, {
    postOrderTransform: tree
  });
  const post = useCallback(async (c_5: UseCommentPost): Promise<void> => {
    if (question_id == null) throw Error('question_id is required');
    if (profile == null) throw Error('profile is required');
    const created_at = DateTime.now().toISO()!;
    const id = 'id' in c_5 ? c_5.id : undefined;
    const {
      error
    } = await (id ? update({
      id,
      cfg: c_5.cfg,
      updated_at: created_at
    }) : insert({
      question_id,
      ...c_5,
      updated_at: created_at,
      created_by: profile!.id
    }).then(r_0 => {
      if (r_0.data) Notify.notifyOnComment({
        question_id,
        id: r_0.data.id,
        mode: id ? 'edit' : 'new'
      }).then(okOrShowError);
      return r_0;
    }));
    if (error) showError(error, 'unable to post/update comment');
  }, [question_id, profile?.id]);
  const deleteComment = useCallback((id_0: string) => update({
    id: id_0,
    deleted: true,
    updated_at: DateTime.now().toISO()
  }), [sb]);
  return {
    comments: comments_0,
    post,
    deleteComment,
    isPending,
    isLoading
  };
};
export const commentStatusSummary = (comments: Pick<CommentItem, 'cfg'>[]) => groupMapObj(comments.filter(c => c.cfg.status?.length > 0 && !c.cfg.resolved).flatMap(c => c.cfg.status.map(s => ({
  status: s,
  ...pick(c.cfg, ['text', 'resolved'])
}))), c => c.status, cs => ({
  ...groupMapObj(cs, c => c.resolved ? 'resolved' : 'unresolved', cs => cs.map(c => c.text))
}));
export type CommentStatusSummary = ReturnType<typeof commentStatusSummary>;
const zCommentUnresolved = z.object({
  question_id: z.string(),
  status: zCommentStatus,
  subquestion_id: z.string().nullish().transform(v => v ?? undefined),
  text: z.string()
});
export type CommentUnresolved = z.infer<typeof zCommentUnresolved>;
export const useCommentsUnresolved = ({
  question_id,
  enabled
}: Pick<CommentQuestion, 'question_id'> & {
  enabled?: boolean;
}) => {
  const {
    data: raw
  } = useQuerySb('comment', ['useCommentsUnresolved', compactObj({
    question_id
  }), {}], async ({
    queryKey: [,, filter]
  }) => {
    const {
      data,
      error
    } = await sb.from('comment_unresolved').select('*').match(filter);
    if (error) throw error;
    return data;
  }, {
    enabled
  });
  const commentsUnresolved = (raw ?? []).map(c => parseOrDefault(zCommentUnresolved, c)).filter(notNull);
  const getComments = useCallback((subquestionId: string): CommentStatusSummary => groupMapObj(commentsUnresolved.filter(c_0 => c_0.subquestion_id === subquestionId), c_1 => c_1.status, cs => ({
    unresolved: cs.map(c_2 => c_2.text),
    resolved: []
  }), {
    returnPartial: true
  }), [raw]);
  return {
    getComments
  };
};
export type UseCommentsUnresolved = ReturnType<typeof useCommentsUnresolved>;
export const sortOptions = ['recent', 'insightful'] as const;
const zSort = z.enum(sortOptions);
export const zCommentQuery = zQuestionQuery.pick({
  subquestion: true
}).extend({
  comment_id: zx.nullish.string,
  comment_hide_forecasts: zx.nullish.bool.default(false),
  comments_expand: zx.nullish.bool,
  sort: zSort.default('recent')
});
export type CommentQuery = z.infer<typeof zCommentQuery>;
export const useCommentExpand = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      key: "comments-expand",
      defaultValue: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [commentExpand, setCommentExpand] = useLocalStorage(t0);
  let t1;
  if ($[1] !== commentExpand || $[2] !== setCommentExpand) {
    t1 = {
      commentExpand,
      setCommentExpand
    };
    $[1] = commentExpand;
    $[2] = setCommentExpand;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};